import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SmoothScroll from "./components/SmoothScroll";
import PageLoading from "./components/PageLoading";
import HomePage from "./pages/HomePage";
import { ADMIN_BASE } from "./lib/adminApi";

const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const IndustriesPage = lazy(() => import("./pages/IndustriesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BookConsultationPage = lazy(() => import("./pages/BookConsultationPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminConsultations = lazy(() => import("./pages/admin/AdminConsultations"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.substring(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function PublicLayout() {
  return (
    <>
      <SmoothScroll />
      <Navbar />
      <main>
        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  const adminPath = ADMIN_BASE.startsWith("/") ? ADMIN_BASE.slice(1) : ADMIN_BASE;

  return (
    <div className="App min-h-screen bg-brand-navy-dark text-white">
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Admin (hidden, not linked from the public site) */}
            <Route path={`${adminPath}/login`} element={<AdminLogin />} />
            <Route path={adminPath} element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="consultations" element={<AdminConsultations />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
            </Route>

            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/industries" element={<IndustriesPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/book-consultation" element={<BookConsultationPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>

        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgba(27, 31, 75, 0.9)",
              border: "1px solid rgba(95, 217, 196, 0.3)",
              color: "#fff",
              backdropFilter: "blur(12px)",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
